import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { status } from 'constants/general';
import { CategoryEnum } from 'constants/categories';
import SearchBar from 'modules/common/components/SearchBar/SearchBar';
import AddBranchProvidersModal from 'modules/modals/AddBranchProvidersModal';
import categoriesStrings from 'modules/categories/strings';
import { branchBookingTypes } from 'constants/bookingTypes';
import { providerTypes } from 'constants/providerTypes';
import initialIcon from 'assets/images/empty-providers.svg';

import Table from 'modules/common/components/Table';
import { TableItemType } from 'modules/common/components/Table/enums';
import DropdownFilter from 'modules/common/components/DropdownFilter';
import themeData from 'utils/theme/themeData';
import { Container, Center, Img, Text, Button, Wrapper } from './ScansProvidersComponent.style';

const ScansProvidersComponent = ({
  isCreateProviderModalHidden,
  setIsCreateProviderModalHidden,
  filtersSelected,
  searchQuery,
  handleSearchBarInputChange,
  handleShowMoreClick,
  handleRemoveProvider,
  handleProviderDeactivate,
  priceListId,
  handleDropDownFilterChange,
}) => {
  const [providersRecords, setProvidersRecords] = useState([]);

  const providers = useSelector(state => state.categories.providers);
  const dropdownsListStatus = useSelector(state => state.categories.dropdownsList.status);
  const areas = useSelector(state => state.categories.dropdownsList.areasList);

  const getMainRowItems = () => {
    const rows = [];
    providers.data?.forEach(item => {
      const { id, provider, active } = item;
      const providerArea = areas?.find(area => area.key === provider?.areaKey);
      const providerName = `${provider?.providerName}${
        providerArea?.name ? ` - ${providerArea?.name}` : ''
      }`;
      rows.push({
        id,
        rowData: item,
        rowContent: [
          {
            itemContent: providerName,
            itemContentURL: provider?.providerPhoto,
            itemType: TableItemType.TextWithPhoto,
            itemPath: `${item.id}`,
            isItemClickable: false,
          },
          {
            itemContent: active,
            itemType: TableItemType.Status,
            isItemClickable: false,
          },
        ],
        editButtons: [
          {
            btnId: 2,
            btnIcon: active ? '"\\e91f"' : '"\\e908"',
            btnLabel: active ? 'Deactivate' : 'Activate',
            btnFn: () => handleProviderDeactivate(item),
          },
          {
            btnId: 1,
            btnIcon: '"\\e91d"',
            btnLabel: 'Remove',
            btnFn: () => handleRemoveProvider(item),
            btnTextColor: themeData.colors.redMain,
          },
        ],
      });
    });

    return rows;
  };

  useEffect(() => {
    setProvidersRecords(() => getMainRowItems());
  }, [providers.data, areas]);

  return (
    <Container>
      {providers.status === status.SUCCESS &&
      providers.data.length === 0 &&
      !searchQuery &&
      Object.keys(filtersSelected).length === 0 ? (
        <Center>
          <Img src={initialIcon} />
          <Text>{categoriesStrings.scansProviders.emptyState}</Text>
          <Button onClick={() => setIsCreateProviderModalHidden(false)}>
            {categoriesStrings.scansProviders.addProvider}
          </Button>
        </Center>
      ) : (
        <Wrapper>
          <Button
            style={{ marginLeft: 'auto' }}
            onClick={() => setIsCreateProviderModalHidden(false)}
          >
            {categoriesStrings.scansProviders.addProvider}
          </Button>
          <SearchBar
            searchQuery={searchQuery}
            handleSearchBarInputChange={handleSearchBarInputChange}
            testId="healthplansearch"
          >
            <DropdownFilter
              dropdownMenuItemsList={areas}
              dropdownMenuPlaceholder={categoriesStrings.filters.areaFilterPlaceholder}
              showLoader={dropdownsListStatus === status.FETCHING}
              handleDropdownMenuChange={value => handleDropDownFilterChange('AreaKey', value)}
              emptyStateText={categoriesStrings.filters.emptyAreaFilter}
            />
          </SearchBar>
          <Table
            latestDataLength={providers.latestDataLength}
            dataStatus={providers.status}
            columns={categoriesStrings.scansProvidersTable.gridHeaderList}
            rows={providersRecords}
            filtersSelected={filtersSelected}
            searchQuery={searchQuery}
            handleShowMoreClick={handleShowMoreClick}
          />
        </Wrapper>
      )}

      {!isCreateProviderModalHidden && (
        <AddBranchProvidersModal
          onClose={() => setIsCreateProviderModalHidden(true)}
          priceListId={priceListId}
          bookingTypeId={branchBookingTypes.PHYSCIAL_SCAN}
          modalStrings={categoriesStrings.addScansModal}
          providerTypeId={providerTypes.SCANS}
        />
      )}
    </Container>
  );
};

ScansProvidersComponent.propTypes = {
  isCreateProviderModalHidden: PropTypes.bool.isRequired,
  setIsCreateProviderModalHidden: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  handleSearchBarInputChange: PropTypes.func.isRequired,
  providersQueryString: PropTypes.shape({
    healthPlanId: PropTypes.number,
    categoryId: PropTypes.number,
    pageNumber: PropTypes.number,
    pagesize: PropTypes.number,
    active: PropTypes.bool,
  }).isRequired,
  handleRemoveProvider: PropTypes.func.isRequired,
  handleProviderDeactivate: PropTypes.func.isRequired,
  handleDropDownFilterChange: PropTypes.func.isRequired,
};

export default ScansProvidersComponent;
